/** SonSpeak brand mark — uses the real logo PNG with graceful fallback to
 *  the original 💬 placeholder if the asset 404s (during local dev / forks).
 *  Two variants:
 *   - <BrandMark /> = just the icon mark (square)
 *   - <BrandLockup /> = icon + "SonSpeak" wordmark (horizontal) */
import { useState } from "react";
import { Link } from "react-router-dom";

export function BrandMark({ size = 128, className = "", testId = "brand-mark-icon" }) {
  const [errored, setErrored] = useState(false);
  if (errored) {
    return (
      <span
        className={`rounded-xl border-[3px] border-[color:var(--ink)] bg-[color:var(--coral)] flex items-center justify-center text-white ${className}`}
        style={{ width: size, height: size }}
        data-testid={testId}
      >💬</span>
    );
  }
  // Natural aspect ratio: set WIDTH only, height=auto so there's no
  // transparent padding above/below the icon.
  return (
    <img
      src="/sonspeak-mark.png"
      alt="SonSpeak"
      onError={() => setErrored(true)}
      className={`block ${className}`}
      style={{ width: size, height: "auto" }}
      data-testid={testId}
    />
  );
}

export function BrandLockup({ to = "/", size = 180, mobileSize = null, className = "" }) {
  const [errored, setErrored] = useState(false);
  // Natural aspect ratio of /sonspeak-logo-horizontal.png is ~16:9 (an
  // even-margin composition with the icon on the right and the wordmark
  // + tagline on the left). Pass `mobileSize` to render smaller below
  // the `sm` breakpoint so the logo never collides with the nav buttons.
  if (errored) {
    return (
      <Link to={to} className={`flex items-center gap-1.5 display text-3xl sm:text-4xl font-extrabold tracking-tight leading-none ${className}`} data-testid="brand-mark">
        <span className="sm:hidden flex-shrink-0"><BrandMark size={Math.min(mobileSize || size, 128)} /></span>
        <span className="hidden sm:inline-flex flex-shrink-0"><BrandMark size={size} /></span>
        <span>SonSpeak</span>
      </Link>
    );
  }
  const desktopWidth = `${size}px`;
  const mobileWidth = `${mobileSize || size}px`;
  return (
    <Link
      to={to}
      className={`block leading-none shrink-0 ${className}`}
      data-testid="brand-mark"
      aria-label="SonSpeak — home"
    >
      {/* Two img tags so we can show different intrinsic widths per
          breakpoint without inflating the on-wire payload — both reference
          the same cached asset. */}
      <img
        src="/sonspeak-logo-transparent.png"
        alt="SonSpeak — empowering every voice"
        onError={() => setErrored(true)}
        style={{ width: mobileWidth, height: "auto" }}
        className="block sm:hidden"
      />
      <img
        src="/sonspeak-logo-transparent.png"
        alt=""
        aria-hidden="true"
        onError={() => setErrored(true)}
        style={{ width: desktopWidth, height: "auto" }}
        className="hidden sm:block"
      />
    </Link>
  );
}

export default BrandLockup;
